import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useContext } from 'react';

import { SymbolMaxLeverageBadge } from '@flipster/features/src/misc/badge';
import { SymbolDisplay } from '@flipster/features/src/misc/display';
import { OrderAttributionMapper, orderAttributionAtom } from '@flipster/features/src/shared/order-attribution';
import {
  SymbolContext,
  symbolButtonBunja,
  zeroSpreadSymbolsBunja,
} from '@flipster/features/src/shared/zero-spread/state';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { getTickerDisplayPrice } from '@flipster/r230522/original/ticker-price';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import webviewHandler from '@flipster/utils/webview-handler';

const SymbolList = () => {
  const { zeroSpreadSymbolsAtom } = useBunja(zeroSpreadSymbolsBunja);
  const zeroSpreadSymbols = useAtomValue(zeroSpreadSymbolsAtom);

  const zeroSpreadSymbolsCount = zeroSpreadSymbols?.length ?? 0;

  return (
    <section>
      <h2 className={css({ textStyle: 'Title/M', mb: 8 })}>
        <FormattedMessage
          id='Autogen_common.r251126.s_Starting_out_with_zeroSpreadSymbolsCount_symbols'
          values={{
            zeroSpreadSymbolsCount: zeroSpreadSymbolsCount,
          }}
        />
      </h2>
      <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
        <FormattedMessage
          id='Autogen_common.r251126.s_Experience_trading_with_zero_spread_with_our_first_zeroSpreadSymbolsCount_symbols_We_ll_continue_to_roll_out_more_symbols_with_zero_spread_stay_tuned'
          values={{
            zeroSpreadSymbolsCount: zeroSpreadSymbolsCount,
          }}
        />
      </p>
      <div className={css({ mt: 16 })}>
        {zeroSpreadSymbols?.map((symbol) => (
          <SymbolContext key={symbol} value={symbol}>
            <SymbolItem />
          </SymbolContext>
        ))}
      </div>
    </section>
  );
};

export default SymbolList;

const SymbolItem = () => {
  const router = useRouter();
  const symbol = useContext(SymbolContext);
  const { symbolInfoAtom, pricePrecisionAtom, maxLeverageHardLimitAtom, maxInitLeverageIsolatedMarginAtom } =
    useBunja(symbolButtonBunja);
  const symbolInfo = useAtomValue(symbolInfoAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const leverage = useAtomValue(maxLeverageHardLimitAtom);
  const isolatedCap = useAtomValue(maxInitLeverageIsolatedMarginAtom);

  const setOrderAttribution = useSetAtom(orderAttributionAtom);

  return (
    <button
      type='button'
      value={symbol}
      onClick={() => {
        webviewHandler.postMessage(
          {
            action: 'link',
            params: {
              page: `trade/perpetual/${symbol}`,
            },
          },
          () => router.push(`/trade/perpetual/${symbol}`),
        );
        setOrderAttribution(OrderAttributionMapper.zeroSpread.zeroSpreadPage);
      }}
      className={flex({
        width: '100%',
        justify: 'space-between',
        align: 'center',
        gap: 24,
        py: 16,
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderColor: 'border.default.level-2',
        _first: {
          borderTopWidth: '1px',
          borderTopStyle: 'solid',
          borderColor: 'border.default.level-2',
        },
      })}
    >
      <div className={flex({ gap: 8, align: 'center' })}>
        <SymbolDisplay
          symbol={symbol}
          icon={<SymbolDisplay.Icon size={32} space={12} />}
          mainLabel={
            <SymbolDisplay.Label
              value='shortName'
              css={{ textStyle: 'Text/L/Strong', color: 'content.default.level-5' }}
            />
          }
          subLabel={<SymbolMaxLeverageBadge crossMax={leverage} isolatedCap={isolatedCap} />}
        />
      </div>
      <div className={stack({ align: 'flex-end' })}>
        <Formatted.Number
          method='round-half-up'
          value={getTickerDisplayPrice(symbolInfo)}
          precision={pricePrecision}
          toFixed
          css={{ color: 'content.default.level-5', textStyle: 'Text/L/Regular' }}
        />
        <Formatted.Percent
          value={symbolInfo?.priceChangePct24h}
          precision={2}
          toFixed
          withColor
          withArrow
          css={{ textStyle: 'Text/L/Regular' }}
        />
      </div>
    </button>
  );
};
